import './Profile.scss';
import '../Tabs.scss';

import { useCallback, useEffect } from 'react';

import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import { Loader, Tab, Tabs } from '@trackman/web-shared-components';
import { ProfessionalLevel } from 'types/certificates';
import { ProfilePicture } from 'components/ProfilePicture/ProfilePicture';
import { ProfileTabsRelativeRoutes } from 'Routes';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { ReactComponent as StarIcon } from 'images/icons/Star.svg';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { getUsername } from 'utilities/userInfo';
import { observer } from 'mobx-react-lite';
import { useGetProfile } from 'queryHooks/profile';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { Outlet, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import { useTabsStore } from 'stores/TabsStore';

export const Profile = observer(() => {
  const navigate = useNavigate();
  const cs = useStores().certificateStore;
  const bs = useStores().billingStore;
  const { t } = useTranslation();
  const { data: profileData, isPending: isProfileDataPending } = useGetProfile();

  useSetPageTitle({ title: t('ProfileSettings', { ns: 'general' }) });

  const { activeTabIndex } = useTabsStore();

  useEffect(() => {
    cs.checkAvailableCertificates();
  }, [cs]);

  useEffect(() => {
    bs.getBillingInfo();
  }, [bs]);

  const setProfessionalLevelTxt = useCallback(() => {
    if (cs.availableCertificates['tmu-Master']) return ProfessionalLevel.Master;
    if (cs.availableCertificates['tmu-Level2']) return ProfessionalLevel.Level2;
    if (cs.availableCertificates['tmu-Level1']) return ProfessionalLevel.Level1;
    return null;
  }, [cs.availableCertificates]);

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{t('ProfileSettings', { ns: 'general' })}</h1>
        <span className='page-subtitle'>{t('CheckEditYourProfile', { ns: 'editProfile' })}</span>
      </div>
      {!isProfileDataPending && !bs.isBillingInfoLoading ? (
        <>
          <div className='profile-title'>{t('EditProfileHeader', { ns: 'editProfile' })}</div>
          <div className='profile-picture-section'>
            <div className='profile-picture-container'>
              <ProfilePicture />
            </div>
            <div className='profile-user-info'>
              <div className='profile-user-name'>
                <p>{t('PlayerName', { ns: 'editProfile' })}</p>
                {!isProfileDataPending && getUsername(profileData?.firstName ?? '', profileData?.lastName ?? '')}
                <span>{!isProfileDataPending && profileData?.email}</span>
              </div>
              <div className='profile-certification-level'>
                <p>
                  {t('TrackmanUniversityCertificationLevel', { ns: 'editProfile' })}
                  {!cs.availableCertificates['tmu-Master'] && (
                    <Tooltip title={t('CompleteCertificationTestsToLevelUp', { ns: 'editProfile' })} placement='bottom'>
                      <InfoIcon />
                    </Tooltip>
                  )}
                </p>
                {cs.isAvailableCertsLoading ? (
                  <Skeleton variant='text' width={200} sx={{ height: '18px' }} />
                ) : (
                  <strong>
                    {t('ProfessionalLevel', { ns: 'editProfile', level: setProfessionalLevelTxt() })}
                    <StarIcon style={!cs.availableCertificates['tmu-Level1'] ? { opacity: 0.2 } : {}} />
                    <StarIcon style={!cs.availableCertificates['tmu-Level2'] ? { opacity: 0.2 } : {}} />
                    <StarIcon style={!cs.availableCertificates['tmu-Master'] ? { opacity: 0.2 } : {}} />
                  </strong>
                )}
              </div>
            </div>
          </div>
          <div className='profile-tabs-wrapper'>
            <>
              <Box className='tabs-container'>
                <Tabs scrollButtons='auto' value={activeTabIndex} variant='scrollable' indicatorColor='primary' allowScrollButtonsMobile>
                  <Tab
                    label={t('PlayerInformation', { ns: 'editProfile' })}
                    onClick={() => navigate(ProfileTabsRelativeRoutes.PlayerInformation)}
                  />
                  <Tab
                    label={t('PlayerSettings', { ns: 'editProfile' })}
                    onClick={() => navigate(ProfileTabsRelativeRoutes.PlayerSettings)}
                  />
                  <Tab
                    label={bs.billingInfoErrorCode ? '' : t('BillingInformation', { ns: 'billingInformation' })}
                    onClick={() => navigate(ProfileTabsRelativeRoutes.BillingAndPayment)}
                  />
                  <Tab
                    label={bs.billingInfoErrorCode ? '' : t('Subscriptions', { ns: 'billingInformation' })}
                    onClick={() => navigate(ProfileTabsRelativeRoutes.Subscriptions)}
                  />
                </Tabs>
              </Box>
              <Outlet />
            </>
          </div>
        </>
      ) : (
        <div className='loader-wrapper'>
          <Loader />
        </div>
      )}
    </>
  );
});

export default Profile;
